import type { api } from "./api";
import type { ColorMode } from "./seatVisuals";

type Seat = Awaited<ReturnType<typeof api.sessionSeating>>["seats"][number];

export interface BreakdownEntry {
  label: string;
  count: number;
}

const MAX_ENTRIES = 6;

function occupancyCategory(seat: Seat): string {
  if (seat.status !== "available" && seat.status !== "reserved") return seat.status;
  return seat.occupant ? "occupied" : "available";
}

/**
 * Counts seats within a group (a row, in practice) by whatever the current
 * color mode distinguishes — e.g. "18 Zusage / 2 Absage" in
 * registrationStatus mode, or the top regions in region mode. Shown on
 * hover for the row-level LOD, where individual seats aren't visible.
 */
export function breakdownFor(seats: Seat[], mode: ColorMode, legendLabel: (key: string) => string): BreakdownEntry[] {
  const counts = new Map<string, number>();

  for (const seat of seats) {
    let key: string | null;
    if (mode === "occupancy") key = occupancyCategory(seat);
    else if (mode === "registrationStatus") key = seat.occupant?.registrationStatus ?? null;
    else if (mode === "region") key = seat.occupant?.region ?? null;
    else key = seat.occupant?.bezirk ?? null;

    if (key === null) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const entries = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([key, count]) => ({ label: legendLabel(key), count }));

  if (entries.length <= MAX_ENTRIES) return entries;
  const shown = entries.slice(0, MAX_ENTRIES);
  const restCount = entries.slice(MAX_ENTRIES).reduce((sum, e) => sum + e.count, 0);
  shown.push({ label: `+${entries.length - MAX_ENTRIES}`, count: restCount });
  return shown;
}
